import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { fetchLiveboardList, fetchLiveboardListWithSearch } from '../services/api';
import { useVariable } from 'context/ThemeContext';

interface LiveboardSearchResult {
  id: string;
  name: string;
}

interface UseLiveboardSearchReturn {
  data: LiveboardSearchResult[];
  loading: boolean;
  error: string | null;
}

export const useLiveboardSearch = (
  searchTerm: string,
  debounceMs = 300
): UseLiveboardSearchReturn => {
  const [data, setData] = useState<LiveboardSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { dispatch } = useVariable();
  const debouncedSearch = useCallback(
    _.debounce(async (term: string) => {
      try {
        setLoading(true);
        setError(null);

        let result;
        if (!term.trim()) {
          result = await fetchLiveboardList();
          dispatch({ type: 'updateLiveboardList', value: result?.objects || [] });
        } else {
          result = await fetchLiveboardListWithSearch(term);
        }

        const transformedData = result?.objects || result || [];
        setData(transformedData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch liveboards');
        setData([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [debounceMs, dispatch]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    if (searchTerm === '') {
      debouncedSearch('');
    }
  }, [debouncedSearch]);

  return {
    data,
    loading,
    error,
  };
};
